import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spin, message } from 'antd';
import styled from 'styled-components';
import { useAuth } from '../../hooks/common/useAuth';
import { PERMISSION_ACTIONS, PERMISSION_SUBJECTS } from '../../constants/service/common/userPermissions';
import { PromotionSchedule } from '../../lib/api/promotionSchedule';
import { CalendarView } from '../../views/promotion/schedule/components/CalendarView';
import { GanttView } from '../../views/promotion/schedule/components/GanttView';
import { ScheduleFormModal } from '../../views/promotion/schedule/components/ScheduleFormModal';
import { TimelineFilter } from '../../views/promotion/schedule/components/TimelineFilter';
import { VIEW_MODE } from '../../views/promotion/schedule/constants';
import { useUpdateScheduleMutation } from '../../views/promotion/schedule/hooks/usePromotionScheduleMutations';
import { useTimelineFilter } from '../../views/promotion/schedule/hooks/useTimelineFilter';
import { promotionScheduleQuery } from '../../views/promotion/schedule/queries';
import { toDatePart } from '../../views/promotion/schedule/utils';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px;
`;

const StyledSpinWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

/**
 * 프로모션 스케줄 타임라인 컨테이너.
 *
 * 필터 상태와 모달 상태만 관리하고 렌더는 뷰에 위임한다.
 * `null` 이 아닌 `modalSchedule` 은 수정 모드, `isCreateOpen` 은 등록 모드 —
 * 두 상태를 분리한 이유는 등록 중에 기존 항목을 클릭해도 폼이 뒤바뀌지 않게 하기 위함.
 */
export const PromotionScheduleContainer = (): React.JSX.Element => {
  const { can } = useAuth();
  const filter = useTimelineFilter();

  const [editingSchedule, setEditingSchedule] = useState<PromotionSchedule | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const updateMutation = useUpdateScheduleMutation();

  const canCreate = can(PERMISSION_ACTIONS.CREATE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);
  const canUpdate = can(PERMISSION_ACTIONS.UPDATE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);
  const canDelete = can(PERMISSION_ACTIONS.DELETE, PERMISSION_SUBJECTS.PROMOTION_SCHEDULE);

  const { data, isLoading } = useQuery({
    ...promotionScheduleQuery.timeline(filter.query),
    keepPreviousData: true,
  });

  const schedules = data?.schedules ?? [];
  const weeks = data?.weeks ?? [];
  const holidays = data?.holidays ?? [];

  const isModalOpen = isCreateOpen || editingSchedule !== null;

  const handleCloseModal = () => {
    setIsCreateOpen(false);
    setEditingSchedule(null);
  };

  /**
   * 간트 막대 드래그·리사이즈로 기간 이동.
   * 실패 메시지는 SuperFetchV2 인터셉터가 에러 모달로 띄우므로 여기서는 성공만 알린다.
   */
  const handleMovePeriod = async (params: { schedule: PromotionSchedule; startDate: string; endDate: string }) => {
    const { schedule, startDate, endDate } = params;

    await updateMutation.mutateAsync({
      id: schedule.id,
      body: { startDate, endDate },
    });
    /**
     * `await` 하지 않는다 — antd `message` 는 메시지가 **닫힐 때**(기본 3초) resolve 되는
     * thenable 이라, 기다리면 GanttView 의 `finally { clearPreview() }` 가 3초 밀린다.
     * 그 사이 다른 막대를 드래그하면 지연된 clearPreview 가 진행 중인 preview 를 지워 막대가 튄다.
     *
     * 기간은 `toDatePart` 로 포맷한다 — 인자는 UTC ISO 라 그대로 보간하면 원문이 노출된다.
     */
    message.success(`"${schedule.title}" 기간을 ${toDatePart(startDate)} ~ ${toDatePart(endDate)} 로 옮겼습니다.`);
  };

  return (
    <StyledContainer>
      <TimelineFilter
        periodUnit={filter.periodUnit}
        range={filter.range}
        tracks={filter.tracks}
        statuses={filter.statuses}
        adminIds={filter.adminIds}
        viewMode={filter.viewMode}
        canCreate={canCreate}
        onChangePeriodUnit={filter.setPeriodUnit}
        onChangeRange={filter.setRange}
        onChangeTracks={filter.setTracks}
        onChangeStatuses={filter.setStatuses}
        onChangeAdminIds={filter.setAdminIds}
        onChangeViewMode={filter.setViewMode}
        onClickCreate={() => setIsCreateOpen(true)}
      />

      {isLoading ? (
        <StyledSpinWrap>
          <Spin />
        </StyledSpinWrap>
      ) : filter.viewMode === VIEW_MODE.GANTT ? (
        <GanttView
          schedules={schedules}
          weeks={weeks}
          holidays={holidays}
          canUpdate={canUpdate}
          onClickSchedule={setEditingSchedule}
          onMovePeriod={handleMovePeriod}
        />
      ) : (
        <CalendarView schedules={schedules} weeks={weeks} holidays={holidays} onClickSchedule={setEditingSchedule} />
      )}

      {isModalOpen && (
        <ScheduleFormModal
          schedule={editingSchedule}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onClose={handleCloseModal}
        />
      )}
    </StyledContainer>
  );
};
